import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  { value: 500, suffix: "+", label: "Activations" },
  { value: 12, suffix: "+", label: "Years" },
  { value: 30, suffix: "+", label: "Brand Partners" },
];

function CountUp({ end, suffix = "", duration = 1600, start }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, end, duration]);

  return (
    <span className="font-display text-4xl sm:text-5xl font-bold text-white tabular-nums">
      {val}
      {suffix}
    </span>
  );
}

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });

  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 px-5 sm:px-8 lg:px-12 bg-[#0A0F1E]"
      data-testid="about-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-[0.4] pointer-events-none" aria-hidden="true">
        <div
          className="absolute -top-20 right-0 w-[520px] h-[520px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(26,86,219,0.35), transparent 70%)" }}
        />
        <div
          className="absolute bottom-0 left-10 w-[360px] h-[360px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(224,36,36,0.15), transparent 70%)" }}
        />
      </div>

      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative max-w-7xl mx-auto"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#60A5FA] font-semibold mb-5">
              <span className="inline-block h-px w-8 align-middle bg-[#60A5FA] mr-3" />
              About the agency
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white"
              data-testid="about-headline"
            >
              We Don't Do{" "}
              <span className="relative inline-block">
                <span className="italic text-white/90">Ordinary</span>
                <span
                  className="absolute left-0 right-0 -bottom-1 h-[3px] bg-[#E02424]"
                  aria-hidden="true"
                />
              </span>
              .
            </h2>
          </div>

          <div className="lg:col-span-7">
            <div
              className="relative rounded-2xl bg-[#1E2A4A] border border-white/5 p-8 sm:p-10 lg:p-12 overflow-hidden"
              data-testid="about-card"
            >
              <span className="absolute left-0 top-8 bottom-8 w-[3px] bg-[#1A56DB] shadow-[0_0_20px_rgba(26,86,219,0.6)]" />
              <p className="text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed">
                <span className="text-white font-semibold">Axe Marketing Services</span> is a specialist BTL
                and experiential marketing agency. We design and execute retail
                activations, in-store promotions, and live brand experiences
                that create real connections between brands and consumers.
              </p>

              <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-5" data-testid="about-stats">
                {stats.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.2 + i * 0.12, duration: 0.6 }}
                    className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-5 sm:px-5 sm:py-6 hover:border-[#60A5FA]/40 transition-colors"
                    data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <CountUp end={s.value} suffix={s.suffix} start={inView} />
                    <div className="mt-2 text-[11px] sm:text-xs tracking-[0.2em] uppercase text-white/50 font-medium">
                      {s.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Partner badges row */}
              <div className="mt-10 pt-8 border-t border-white/5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-semibold mb-4">
                  Trusted by
                </div>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[11px] text-white/60"
                      data-testid={`partner-badge-${i}`}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#60A5FA]" />
                      Brand Partner
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
